import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import chunk from 'lodash/chunk'
import isEqual from 'lodash/isEqual'
import React from 'react'
import { ControllerRenderProps, FieldValues } from 'react-hook-form'
import { FormattedMessage } from 'react-intl'

import s from './styles.module.scss'

import Icon from '../../atoms/icons/icon'
import { useLocale } from '../../../hooks/use-locale'
import { cn } from '../../../shadcn/utils'
import { capitalizeFirstLetter } from '../../../utils'
import { convertTimezonedDateToIsoAtMidnight } from '../../../utils/date-helpers'

dayjs.extend(utc)

type DatePickerCalendarFlexibleProps = {
  field: ControllerRenderProps<FieldValues, string>
  onDatesSelected?: (value: boolean) => void
  closeModal?: () => void | null
  allowMultiDates: boolean
  onValid?: () => void
}

type Month = {
  monthName: string
  year: number
  startDate: Date
  endDate: Date
}

interface MonthCardProps extends Omit<Month, 'startDate' | 'endDate'> {
  isActive: boolean
  onClick: (event: React.MouseEvent) => void
}

type Dates = { startDate: Date; endDate: Date }

const useFlexibleCalendar = (allowMultiDates: boolean) => {
  const { locale } = useLocale()
  const [calendar, setCalendar] = React.useState<Month[]>([])
  dayjs.locale(locale)

  React.useEffect(() => {
    const today = dayjs()
    const endOfCurrentMonth = today.endOf('month')
    const daysRemaining = endOfCurrentMonth.diff(today, 'day')

    let startMonth = today
    const needToMoveToNextMonth = daysRemaining < 3 // 3 days because we select 2 days / 1 night, and the current is disabled by default
    if (needToMoveToNextMonth) {
      startMonth = today.add(1, 'month') // Move to the next month
    }

    // Create an array with 12 months starting from startMonth
    const months = []
    for (let i = 0; i < 12; i++) {
      const month = startMonth.add(i, 'month')
      let startDate = month.startOf('month')
      if (!needToMoveToNextMonth && i === 0) {
        startDate = today.add(1, 'day')
      }
      const endDate = startDate.add(1, 'day')

      months.push({
        monthName: month.format('MMMM'),
        year: month.year(),
        startDate: convertTimezonedDateToIsoAtMidnight(startDate.toDate()),
        endDate: convertTimezonedDateToIsoAtMidnight(
          allowMultiDates ? endDate.toDate() : startDate.toDate(),
        ),
      })
    }

    setCalendar(months)
  }, [])

  return calendar
}

const MonthCard: React.FC<MonthCardProps> = ({
  monthName,
  year,
  isActive,
  onClick,
}) => {
  return (
    <div
      className={cn(
        'flex cursor-pointer flex-col items-center justify-center gap-4 rounded-[10px]',
        'w-full border p-4 text-base text-black',
        isActive ? 'border-black bg-grey-300' : 'border-grey-400 bg-white',
        'hover:border-black',
      )}
      onClick={onClick}
    >
      <Icon name='calendar' size={24} className='flex-shrink-0' />
      <h4 className='font-bold'>{capitalizeFirstLetter(monthName)}</h4>
      <p className='text-grey-700'>{year}</p>
    </div>
  )
}

export const DatePickerCalendarFlexible: React.FC<
  DatePickerCalendarFlexibleProps
> = ({ field, onDatesSelected, closeModal, allowMultiDates, onValid }) => {
  const calendar = useFlexibleCalendar(allowMultiDates)
  const groupedCalendar = chunk(calendar, 4)

  const setActiveMonth = ({ startDate, endDate }: Dates) => {
    if (!field.value?.startDate || !field.value?.endDate) {
      return false
    }
    const fieldStartDate = field.value.startDate
    const fieldEndDate = field.value.endDate
    return isEqual(startDate, fieldStartDate) && isEqual(endDate, fieldEndDate)
  }

  const setSelectedDates = ({ startDate, endDate }: Dates) => {
    field.onChange({ startDate, endDate, flexible: true })
    onDatesSelected?.(true)
  }

  const handleClick = (dates: Dates) => (e: React.MouseEvent) => {
    e.stopPropagation()
    setSelectedDates(dates)
    closeModal?.()
    onValid?.()
  }

  return (
    <div>
      <h3 className='text-left text-base font-bold leading-none'>
        <FormattedMessage
          id='ui.DatePickerCalendarFlexible.title'
          defaultMessage='Quand voulez-vous partir ?'
        />
      </h3>
      <div className='pb-3 pt-5'>
        <div
          className={cn(
            'md:grid-row-3 flex w-full snap-none flex-col gap-4 overflow-hidden',
            'md:grid lg:flex lg:snap-x lg:snap-mandatory lg:flex-row lg:gap-0 lg:space-x-4 lg:overflow-x-auto lg:scroll-smooth lg:pb-1',
            s.scrollbarVisible,
          )}
        >
          {groupedCalendar.map((group, index) => (
            <div
              className='grid grid-cols-2 gap-4 md:flex lg:w-full lg:flex-shrink-0 lg:snap-center lg:snap-always'
              key={`group-month-${index}`}
            >
              {group.map(({ monthName, year, ...dates }) => (
                <MonthCard
                  key={`month-${monthName}-${year}`}
                  monthName={monthName}
                  year={year}
                  isActive={setActiveMonth(dates)}
                  onClick={handleClick(dates)}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
